import React, { useMemo, useState } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { MINING_ADDRESS, RPC_URL, TOKEN_ADDRESS } from '../config';
import miningAbi from '../abis/MiningContract.json';
import tokenAbi from '../abis/TokenContract.json';

export default function UserQuery() {
    const provider = useMemo(() => new ethers.JsonRpcProvider(RPC_URL), []);
    const [queryAddress, setQueryAddress] = useState('');
    const [loading, setLoading] = useState(false);
    const [userInfo, setUserInfo] = useState(null);

    const formatDate = (timestamp) => {
        if (!timestamp || Number(timestamp) === 0) return '无';
        return new Date(Number(timestamp) * 1000).toLocaleString('zh-CN');
    };

    const formatNum = (val, unit = '') => {
        const num = parseFloat(val);
        const formatted = isNaN(num) ? '0.0000000' : num.toFixed(7);
        return unit ? `${formatted} ${unit}` : formatted;
    };

    const getLevelName = (level) => {
        const levels = {
            0: '普通用户',
            1: 'V1',
            2: 'V2',
            3: 'V3',
            4: 'V4',
            5: 'V5',
            6: 'V6'
        };
        return levels[level] || '普通用户';
    };

    const fetchUserInfo = async () => {
        if (!ethers.isAddress(queryAddress)) {
            toast.error('请输入有效的地址');
            return;
        }
        setLoading(true);
        try {
            const miningContract = new ethers.Contract(
                MINING_ADDRESS,
                miningAbi,
                provider
            );
            const tokenContract = new ethers.Contract(
                TOKEN_ADDRESS,
                tokenAbi,
                provider
            );

            const [
                basicInfo,
                bnbBalance,
                tokenBalance,
                claimableDividend,
                nextClaimTime,
                shareholderInfo,
                pendingRewards,
                pendingCertificates,
                pendingEmptyOrders
            ] = await Promise.all([
                miningContract.users(queryAddress),
                provider.getBalance(queryAddress),
                tokenContract.balanceOf(queryAddress).catch(() => 0n),
                miningContract.getClaimableDividend(queryAddress).catch(() => 0n),
                miningContract.getNextClaimTime(queryAddress).catch(() => 0n),
                miningContract.shareholders(queryAddress).catch(() => ({
                    orderAmount: 0n,
                    lastTotalPool: 0n,
                    lastClaimTime: 0n,
                    totalClaimed: 0n,
                    isActive: false
                })),
                miningContract.getPendingRewards(queryAddress).catch(() => [0n, 0n]),
                miningContract.pendingCertificates(queryAddress).catch(() => 0n),
                miningContract.pendingEmptyOrders(queryAddress).catch(() => 0n)
            ]);

            let specialOrderInfo = null;
            try {
                const specialOrder = await miningContract.specialOrders(queryAddress);
                if (specialOrder && Number(specialOrder.startTime) > 0) {
                    specialOrderInfo = {
                        certificateAmount: ethers.formatUnits(specialOrder.certificateAmount, 18),
                        totalAmount: ethers.formatUnits(specialOrder.certificateAmount, 18),
                        releasedAmount: ethers.formatUnits(specialOrder.totalReleased, 18),
                        startTime: specialOrder.startTime.toString(),
                        totalReleased: ethers.formatUnits(specialOrder.totalReleased, 18),
                        totalWithdrawn: ethers.formatUnits(specialOrder.totalWithdrawn, 18)
                    };
                }
            } catch (err) {
                console.log('无锁仓订单信息');
            }

            setUserInfo({
                address: queryAddress,
                bnbBalance: ethers.formatEther(bnbBalance),
                usdtBalance: ethers.formatUnits(tokenBalance, 18),
                totalDeposit: ethers.formatEther(basicInfo.totalDeposit),
                teamSize: basicInfo.teamSize.toString(),
                teamPerformance: ethers.formatUnits(basicInfo.teamTotalPerformance, 18),
                maxTeamPerformance: ethers.formatUnits(basicInfo.teamMaxSubPerformance, 18),
                otherTeamPerformance: ethers.formatUnits(basicInfo.teamTotalPerformance - basicInfo.teamMaxSubPerformance, 18),
                lastMiningTime: basicInfo.lastRewardUpdate.toString(),
                level: basicInfo.manualLevel.toString(),
                realLevel: basicInfo.realLevel.toString(),
                maxLevel: basicInfo.maxLevel.toString(),
                levelExpireTime: basicInfo.levelExpireTime.toString(),
                isRegistered: basicInfo.isRegistered,
                hasPendingCertificates: pendingCertificates > 0n,
                hasPendingEmptyOrders: pendingEmptyOrders > 0n,
                pendingCertificatesAmount: ethers.formatUnits(pendingCertificates, 18),
                pendingEmptyOrdersAmount: ethers.formatUnits(pendingEmptyOrders, 18),
                totalDepositUsdt: ethers.formatUnits(basicInfo.totalDepositUsdt, 18),
                isShareholder: shareholderInfo.isActive,
                orderAmount: ethers.formatUnits(shareholderInfo.orderAmount, 18),
                totalClaimed: ethers.formatEther(shareholderInfo.totalClaimed),
                totalWithdrawn: ethers.formatUnits(basicInfo.totalWithdrawn, 18),
                claimableDividend: ethers.formatUnits(claimableDividend, 18),
                nextClaimTime: nextClaimTime.toString(),
                specialOrder: specialOrderInfo,
                normalReward: ethers.formatUnits(pendingRewards[0], 18),
                specialReward: ethers.formatUnits(pendingRewards[1], 18)
            });
        } catch (err) {
            console.error('获取用户信息失败:', err);
            toast.error('获取用户信息失败: ' + err.message);
            setUserInfo(null);
        } finally {
            setLoading(false);
        }
    };

    const isLevelInExpiry = userInfo ? Number(userInfo.levelExpireTime) > Math.floor(Date.now() / 1000) : false;
    const realLevelNum = userInfo ? Number(userInfo.realLevel) || 0 : 0;
    const manualLevelNum = userInfo ? Number(userInfo.level) || 0 : 0;
    const displayLevel = userInfo ? (isLevelInExpiry ? Math.max(realLevelNum, manualLevelNum) : realLevelNum) : 0;

    return (
        <div className="container">
            <div className="card">
                <h2 className="card-title">查询</h2>
                <div className="form-group">
                    <label className="form-label">用户地址</label>
                    <input
                        type="text"
                        className="form-input"
                        value={ queryAddress }
                        onChange={ (e) => setQueryAddress(e.target.value.trim()) }
                        placeholder="0x..."
                    />
                </div>
                <button className="action-btn" onClick={ fetchUserInfo } disabled={ loading }>
                    { loading ? '查询中...' : '查询' }
                </button>
            </div>

            { userInfo && (
                <>
                    <div className="card">
                        <h2 className="card-title">基础信息</h2>
                        <div className="info-row">
                            <span className="info-label">地址</span>
                            <span className="info-value">{ userInfo.address }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">等级</span>
                            <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                { getLevelName(displayLevel) }
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">注册状态</span>
                            <span className="info-value">{ userInfo.isRegistered ? '已注册' : '未注册' }</span>
                        </div>
                        { userInfo.hasPendingCertificates && (
                            <div className="info-row">
                                <span className="info-label">未领取锁仓订单</span>
                                <span className="info-value">
                                    有（{ formatNum(userInfo.pendingCertificatesAmount, 'USD') }）
                                </span>
                            </div>
                        ) }
                        { userInfo.hasPendingEmptyOrders && (
                            <div className="info-row">
                                <span className="info-label">未领取订单</span>
                                <span className="info-value">
                                    有（{ formatNum(userInfo.pendingEmptyOrdersAmount, 'USD') }）
                                </span>
                            </div>
                        ) }
                    </div>

                    <div className="card">
                        <h2 className="card-title">额度与收益</h2>
                        <div className="info-row">
                            <span className="info-label">总质押 (BNB)</span>
                            <span className="info-value">{ formatNum(userInfo.totalDeposit, 'BNB') }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">总质押价值 (USD)</span>
                            <span className="info-value">{ formatNum(userInfo.totalDepositUsdt, 'USD') }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">总额度</span>
                            <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                { formatNum(parseFloat(userInfo.totalDepositUsdt) * 6, 'USD') }
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">剩余额度</span>
                            <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                { formatNum(parseFloat(userInfo.totalDepositUsdt) * 6 - parseFloat(userInfo.totalWithdrawn), 'USD') }
                            </span>
                        </div>
                    </div>

                    <div className="card">
                        <h2 className="card-title">待提取奖励</h2>
                        <div className="info-row">
                            <span className="info-label">普通质押奖励 (HP)</span>
                            <span className="info-value">{ formatNum(userInfo.normalReward, 'HP') }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">线性释放奖励 (HP)</span>
                            <span className="info-value">{ formatNum(userInfo.specialReward, 'HP') }</span>
                        </div>
                        { (userInfo.isShareholder || userInfo.specialOrder) && (
                            <div className="info-row">
                                <span className="info-label">待提取分红 (BNB)</span>
                                <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                    { formatNum(userInfo.claimableDividend, 'BNB') }
                                </span>
                            </div>
                        ) }
                    </div>

                    <div className="card">
                        <h2 className="card-title">团队信息</h2>
                        <div className="info-row">
                            <span className="info-label">团队人数</span>
                            <span className="info-value">{ userInfo.teamSize } 人</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">团队业绩</span>
                            <span className="info-value">{ formatNum(userInfo.teamPerformance, 'BNB') }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">大区业绩</span>
                            <span className="info-value">{ formatNum(userInfo.maxTeamPerformance, 'BNB') }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">小区业绩</span>
                            <span className="info-value">{ formatNum(userInfo.otherTeamPerformance, 'BNB') }</span>
                        </div>
                    </div>
                    { userInfo.specialOrder && (
                        <div className="card">
                            <h2 className="card-title">线性释放</h2>
                            <>
                                <div className="info-row">
                                    <span className="info-label">锁仓订单金额</span>
                                    <span className="info-value">{ formatNum(userInfo.specialOrder.certificateAmount, 'USD') }</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">累计线性释放总量</span>
                                    <span className="info-value">{ formatNum(userInfo.specialOrder.totalReleased, 'HP') }</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">累计产生收益(包含未解锁)</span>
                                    <span className="info-value">{ formatNum(userInfo.specialOrder.totalWithdrawn, 'USD') }</span>
                                </div>
                            </>
                        </div>
                    ) }
                    { (userInfo.isShareholder) && (
                        <div className="card">
                            <h2 className="card-title">股东与分红</h2>

                            <div className="info-row">
                                <span className="info-label">股东金额权重</span>
                                <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                    { formatNum(userInfo.orderAmount, 'USD') }
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">可提取分红</span>
                                <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                    { formatNum(userInfo.claimableDividend, 'BNB') }
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">累计领取分红</span>
                                <span className="info-value">{ formatNum(userInfo.totalClaimed, 'BNB') }</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">下次提取时间</span>
                                <span className="info-value">{ formatDate(userInfo.nextClaimTime) }</span>
                            </div>


                            { userInfo.isShareholder && (
                                <>
                                    <div className="divider"></div>
                                    <div className="info-row">
                                        <span className="info-label">股东分红资格</span>
                                        <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                            已激活
                                        </span>
                                    </div>
                                </>
                            ) }
                        </div>
                    ) }

                    <button className="action-btn" onClick={ fetchUserInfo } style={ { marginTop: '20px', width: '100%' } } disabled={ loading }>
                        刷新数据
                    </button>
                </>
            ) }
        </div>
    );
}
